// Certificações da própria pessoa em "Meu cadastro" (`GET /me/person/certifications`; REQ-PPL-009, 010; Parte 6.5): leitura com o
// SEMÁFORO de validade (válida, a vencer, vencida, revogada — sempre com o texto, nunca só cor) e a situação de verificação por
// segunda pessoa. A pessoa não edita a certificação (quem registra é a coordenação e quem verifica é outra pessoa — SoD).
// TODO(GIY-083): o envio de comprovante pela própria pessoa chega com o módulo de documentos; até lá a tela só avisa.
import {
  Badge,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@giyus/ui';
import { useTranslation } from 'react-i18next';

import { ProblemAlert } from '../../../components';
import { useDateFormatter } from '../../../lib/dates/useDateFormatter';
import { useCertificationTypes, useMyCertifications } from '../api/queries';
import type { PeopleScope } from '../api/scope';
import { useCivilDate } from '../lib/civilDateDisplay';

type Variant = 'success' | 'warning' | 'destructive' | 'outline';

function statusVariant(status: string): Variant {
  switch (status) {
    case 'Valid':
      return 'success';
    case 'Expiring':
      return 'warning';
    case 'Expired':
    case 'Revoked':
      return 'destructive';
    default:
      return 'outline';
  }
}

/** `catalogScope` só serve para ler os nomes do catálogo de tipos (referência de rede); `null` mostra o código. */
export function MyCertificationsCard({
  catalogScope,
}: {
  readonly catalogScope: PeopleScope | null;
}) {
  const { t } = useTranslation('people');
  const civil = useCivilDate();
  const dates = useDateFormatter();
  const certifications = useMyCertifications();
  return (
    <section
      className="flex flex-col gap-3 rounded-card border p-4"
      aria-labelledby="my-certifications-title"
      data-testid="my-certifications"
    >
      <div>
        <h2 id="my-certifications-title" className="text-title font-semibold">
          {t('me.certifications.title')}
        </h2>
        <p className="text-body text-muted-foreground">{t('me.certifications.description')}</p>
      </div>
      {certifications.isError ? (
        <ProblemAlert
          error={certifications.error}
          onRetry={() => {
            void certifications.refetch();
          }}
        />
      ) : certifications.isLoading || !certifications.data ? (
        <Skeleton className="h-24 w-full" />
      ) : certifications.data.length === 0 ? (
        <p className="text-body text-muted-foreground" data-testid="my-certifications-empty">
          {t('me.certifications.empty')}
        </p>
      ) : (
        <Table
          regionLabel={t('me.certifications.tableLabel')}
          data-testid="my-certifications-table"
        >
          <TableHeader>
            <TableRow>
              <TableHead>{t('me.certifications.columns.type')}</TableHead>
              <TableHead>{t('me.certifications.columns.status')}</TableHead>
              <TableHead>{t('me.certifications.columns.validUntil')}</TableHead>
              <TableHead>{t('me.certifications.columns.verification')}</TableHead>
              <TableHead>{t('me.certifications.columns.evidence')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {certifications.data.map((item) => (
              <TableRow key={item.id} data-testid={`my-certification-${item.id}`}>
                <TableCell>
                  {catalogScope ? (
                    <CertificationName scope={catalogScope} code={item.typeCode} />
                  ) : (
                    <span className="font-mono text-caption">{item.typeCode}</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={statusVariant(item.status)}
                    data-testid={`my-certification-status-${item.id}`}
                  >
                    {t(`common.credentialStatus.${item.status}`, { defaultValue: item.status })}
                  </Badge>
                </TableCell>
                <TableCell>{civil(item.validUntil)}</TableCell>
                <TableCell>
                  {item.verifiedAt
                    ? t('me.certifications.verifiedAt', { when: dates.dateTime(item.verifiedAt) })
                    : t('me.certifications.pending')}
                </TableCell>
                <TableCell>
                  {item.evidenceDocumentId
                    ? t('me.certifications.evidenceYes')
                    : t('me.certifications.evidenceNo')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <p
        className="text-caption text-muted-foreground"
        data-testid="my-certifications-evidence-note"
      >
        {t('me.certifications.evidenceNote')}
      </p>
    </section>
  );
}

/** Nome do tipo no catálogo de rede (espelho do tenant); o código serve de reserva enquanto carrega ou se o tipo sumiu. */
function CertificationName({
  scope,
  code,
}: {
  readonly scope: PeopleScope;
  readonly code: string;
}) {
  const types = useCertificationTypes(scope);
  const name = types.data?.find((item) => item.code === code)?.name;
  return (
    <span>
      {name ?? code}
      {name ? (
        <span className="block font-mono text-caption text-muted-foreground">{code}</span>
      ) : null}
    </span>
  );
}
